import React, { useState, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Search, PlayCircle, Sparkles
} from 'lucide-react';

const countries = [
  { code: 'MCO', label: 'Colombia', flag: '🇨🇴' },
  { code: 'MLA', label: 'Argentina', flag: '🇦🇷' },
  { code: 'MLM', label: 'México', flag: '🇲🇽' },
  { code: 'MLC', label: 'Chile', flag: '🇨🇱' },
  { code: 'MPE', label: 'Perú', flag: '🇵🇪' },
  { code: 'MLB', label: 'Brasil', flag: '🇧🇷' },
  { code: 'MLU', label: 'Uruguay', flag: '🇺🇾' },
];

export default function SearchProductsPage() {
  const [country, setCountry] = useState('MLM');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('demand');
  const requestId = useRef(0);

  const selectedCountry = countries.find(c => c.code === country);

  const handleSearch = async () => {
    if (!query.trim()) return toast.error('Ingresa un producto o nicho');
    const currentRequest = ++requestId.current;
    setSearching(true);
    try {
      const res = await api.post('/research/analyze', { query: query.trim(), site: country });
      if (currentRequest === requestId.current) setResults(res.data);
      toast.success('Análisis completado');
    } catch (error) {
      if (currentRequest === requestId.current) {
        setResults(null);
        toast.error(error.response?.data?.detail || 'Error al analizar');
      }
    } finally {
      setSearching(false);
    }
  };

  return (
    <DashboardLayout
      title="Búsqueda de Productos"
      subtitle="Analiza nichos en Mercado Libre"
      icon={Search}
      backTo="/dashboard"
      headerRight={
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800">
            <PlayCircle size={16} />
            Ver tutorial
          </button>
        </div>
      }
    >
      {/* Search Section */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
        <div className="flex items-center gap-3">
          {/* Country Selector */}
          <div className="relative">
            <button
              data-testid="country-selector"
              onClick={() => setShowCountryMenu(!showCountryMenu)}
              className="flex items-center gap-2 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white hover:bg-slate-700 transition-colors min-w-[160px]"
            >
              <span className="text-lg">{selectedCountry?.flag}</span>
              {selectedCountry?.label}
              <span className="ml-auto text-slate-400">▼</span>
            </button>
            {showCountryMenu && (
              <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 w-48">
                {countries.map(c => (
                  <button
                    key={c.code}
                    onClick={() => { setCountry(c.code); setShowCountryMenu(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-slate-700 ${
                      country === c.code ? 'text-blue-400' : 'text-white'
                    }`}
                  >
                    <span className="text-lg">{c.flag}</span>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search Input */}
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="research-search"
              type="text"
              placeholder="Buscar producto o nicho (ej: auriculares bluetooth)"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500"
            />
          </div>

          {/* Search Button */}
          <button
            data-testid="research-analyze-btn"
            onClick={handleSearch}
            disabled={searching}
            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 transition-all"
          >
            <Search size={16} />
            {searching ? 'Analizando...' : 'Analizar'}
          </button>
        </div>

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-slate-500">
            Introduce el nombre del producto que quieres investigar, idealmente usando 2-3 palabras. Ejemplo: "reloj digital".
          </p>
          <span className="flex items-center gap-1 text-xs text-slate-400"><Sparkles size={12} className="text-blue-400" />Muestra máxima: 25 publicaciones · vendidos históricos como proxy</span>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="mt-6 bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Resultados del Análisis</h3>
          <p className="text-sm text-slate-400 mb-4">{results.verdict}</p>
          <div className="flex gap-2 border-b border-slate-800 mb-4">
            {[['demand', 'Demanda'], ['market', 'Mercado'], ['strategy', 'Estrategia']].map(([id, label]) => <button key={id} onClick={() => setActiveTab(id)} className={`px-4 py-2 text-sm ${activeTab === id ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400'}`}>{label}</button>)}
          </div>
          {activeTab === 'demand' && <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm"><Metric label="Base de demanda" value={results.demand_basis} /><Metric label="Vendidos" value={results.total_sold ?? '—'} /><Metric label="Muestra" value={`${results.analyzed_count}/${results.results_count}`} /><Metric label="Visitas 30d" value="—" /></div>}
          {activeTab === 'market' && <div className="overflow-x-auto"><table className="w-full text-sm text-slate-300"><thead><tr className="text-left text-slate-500"><th className="p-2">Publicación</th><th className="p-2">Precio</th><th className="p-2">Vendidos</th><th className="p-2">Envío FULL</th></tr></thead><tbody>{(results.items || []).map(item => <tr key={item.item_id} className="border-t border-slate-800"><td className="p-2"><a className="text-blue-400" href={item.permalink} target="_blank" rel="noreferrer">{item.title}</a></td><td className="p-2">{item.price ?? '—'} {item.currency_id || ''}</td><td className="p-2">{item.sold_quantity ?? '—'}</td><td className="p-2">{item.is_full == null ? '—' : item.is_full ? 'Sí' : 'No'}</td></tr>)}</tbody></table></div>}
          {activeTab === 'strategy' && <div className="text-sm text-slate-400 space-y-2"><p>{results.verdict}</p><p>La muestra contiene {results.analyzed_count} publicaciones. No se presentan estimaciones mensuales porque esta búsqueda no tiene visitas verificadas de competidores.</p></div>}
        </div>
      )}
    </DashboardLayout>
  );
}

function Metric({ label, value }) {
  return <div className="bg-slate-800/60 rounded-lg p-3"><p className="text-xs text-slate-500">{label}</p><p className="text-white mt-1">{value}</p></div>;
}
